// DEMO sandbox banner — only renders when the signed-in session is demo mode.
// Provides an always-visible "you're in demo" indicator, a no-password persona
// switcher, and a reset-to-clean-state action.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, ChevronDown, RotateCcw, Loader2, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const ROLE_LABELS = {
    director: "Director", administrator: "Administrator", consultant: "Consultant",
    client_admin: "Client Admin", site_manager: "Site Manager", reviewer: "Reviewer",
};
const ROLE_TONE = {
    director: "#C75A1B", administrator: "#2A5DB0", consultant: "#2E7D5B",
    client_admin: "#6B4E9E", site_manager: "#A8501F", reviewer: "#6F6A64",
};

export default function DemoBar() {
    const { user, demoLogin, resetDemo } = useAuth();
    const [open, setOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [switching, setSwitching] = useState(null);
    const [resetting, setResetting] = useState(false);
    const ref = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        api.get("/auth/demo-users").then((r) => setUsers(r.data)).catch(() => {});
    }, []);

    if (!user || user.mode !== "demo") return null;

    async function switchTo(u) {
        if (u.id === user.id) { setOpen(false); return; }
        setSwitching(u.id);
        const res = await demoLogin(u.id);
        setSwitching(null);
        setOpen(false);
        if (res.ok) {
            toast.success(`Now viewing as ${u.name}`, { description: ROLE_LABELS[u.role] || u.role });
            nav("/app/dashboard", { replace: true });
        } else {
            toast.error(res.error || "Could not switch user");
        }
    }

    async function onReset() {
        if (!window.confirm("Reset the demo sandbox to a clean state? This wipes all demo changes and restores the original sample data.")) return;
        setResetting(true);
        const res = await resetDemo();
        setResetting(false);
        if (res.ok) {
            toast.success("Demo data reset", { description: "Reloading the clean sandbox…" });
            setTimeout(() => window.location.reload(), 700);
        } else {
            toast.error(res.error || "Reset failed");
        }
    }

    return (
        <div data-testid={TID.demoBanner}
             className="flex items-center gap-3 px-5 py-2 text-white"
             style={{ background: "linear-gradient(90deg,#C75A1B 0%,#9F4212 100%)" }}>
            <FlaskConical className="w-4 h-4 shrink-0" />
            <span className="text-[12px] font-semibold">Demo sandbox</span>
            <span className="text-[11.5px] hidden sm:inline" style={{ color: "rgba(255,255,255,.85)" }}>
                Sample data — changes here never affect live.
            </span>

            <div className="ml-auto flex items-center gap-2">
                {/* Persona switcher */}
                <div className="relative" ref={ref}>
                    <button
                        data-testid={TID.demoSwitchTrigger}
                        onClick={() => setOpen((o) => !o)}
                        className="inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-[12px] font-semibold border border-white/25"
                    >
                        <span className="w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold shrink-0"
                              style={{ background: "#fff", color: ROLE_TONE[user.role] || "#9F4212" }}>
                            {user.avatar_initials || user.name?.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="max-w-[160px] truncate">{user.name}</span>
                        <span className="hidden md:inline text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-white/20">
                            {ROLE_LABELS[user.role] || user.role}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5" />
                    </button>

                    {open && (
                        <div data-testid={TID.demoSwitchMenu}
                             className="absolute right-0 mt-2 w-[300px] bg-white rounded-[12px] shadow-brand-lg border z-50 overflow-hidden"
                             style={{ borderColor: "#E5E0DA" }}>
                            <div className="px-3.5 py-2.5 border-b flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                                <Users className="w-3.5 h-3.5" style={{ color: "#9F4212" }} />
                                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#6F6A64" }}>Switch user (no password)</span>
                            </div>
                            <div className="max-h-[320px] overflow-y-auto py-1">
                                {users.map((u) => (
                                    <button
                                        key={u.id}
                                        data-testid={TID.demoSwitchPersona(u.role)}
                                        onClick={() => switchTo(u)}
                                        disabled={switching !== null}
                                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[#FBEFE5] transition-colors disabled:opacity-60"
                                    >
                                        <span className="w-8 h-8 rounded-[8px] grid place-items-center text-[10.5px] font-bold text-white shrink-0"
                                              style={{ background: ROLE_TONE[u.role] || "#6F6A64" }}>
                                            {u.avatar_initials || u.name?.slice(0, 2).toUpperCase()}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{u.name}</div>
                                            <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>
                                                {ROLE_LABELS[u.role] || u.role}{u.client_name ? ` · ${u.client_name}` : ""}
                                            </div>
                                        </div>
                                        {switching === u.id
                                            ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: "#9F4212" }} />
                                            : (u.id === user.id && <Check className="w-4 h-4 shrink-0" style={{ color: "#2E7D5B" }} />)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Reset */}
                <button
                    data-testid={TID.demoResetData}
                    onClick={onReset}
                    disabled={resetting}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-[12px] font-semibold border border-white/25 disabled:opacity-60"
                    title="Reset demo data to a clean state"
                >
                    {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{resetting ? "Resetting…" : "Reset data"}</span>
                </button>
            </div>
        </div>
    );
}
